"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFloatingSearch = exports.useOptionalFloatingSearch = exports.FloatingSearchContext = exports.useSearchable = exports.useOptionalSearchable = exports.SearchableContext = exports.formatBooleanTag = exports.normalize = exports.resolveFieldValue = void 0;
exports.useSearchableCore = useSearchableCore;
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const OverlayContext_1 = require("../contexts/OverlayContext");
const ThemeContext_1 = require("../ThemeContext");
const resolveFieldValue = (item, resolver) => {
    if (item == null || resolver == null)
        return '';
    if (typeof resolver === 'function') {
        return resolver(item);
    }
    if (typeof item !== 'object') {
        return item;
    }
    const obj = item;
    if (typeof resolver === 'string' && resolver in obj) {
        return obj[resolver];
    }
    return '';
};
exports.resolveFieldValue = resolveFieldValue;
const normalize = (s) => {
    return String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
};
exports.normalize = normalize;
const formatBooleanTag = (key, isTrue = true, overrides) => {
    const stringKey = String(key);
    const override = overrides?.[stringKey];
    if (typeof override === "object" && override !== null) {
        if (isTrue && override.trueLabel)
            return override.trueLabel;
        if (!isTrue && override.falseLabel)
            return override.falseLabel;
    }
    const baseKey = typeof override === "string"
        ? override
        : stringKey.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toUpperCase();
    return isTrue ? baseKey : `NO ${baseKey}`;
};
exports.formatBooleanTag = formatBooleanTag;
function useSearchableCore({ list = [], loading = false, valueKey = 'value', labelKey = 'label', searchFields = ['label'], groupBy, groupTitleKey, activeColor = "primary", backgroundColor: rawBackgroundColor, collapsibleSections = false, booleanTagOverrides = {}, isOpen: controlledOpen, setIsOpen: setControlledOpen, disabled = false, // for disabled state, we can disable the input and the dropdown
closeWhenTextCleared = false, openOnClick = false, onSelect, closeOnSelect, multiple = false, required = true, // set to true for single select to avoid unselecting the value
selectedValue: controlledSelectedValue, setSelectedValue: setControlledSelectedValue, clearSearchOnSelect = false, disableInternalFilter = false, searchable = true, } = {}) {
    list = list ?? [];
    const floatingId = react_1.default.useId();
    const { t } = (0, react_i18next_1.useTranslation)();
    const THEME = (0, ThemeContext_1.useTheme)();
    const { registerFloating, unregisterFloating } = (0, OverlayContext_1.useOverlay)();
    const [uncontrolledOpen, setUncontrolledOpen] = react_1.default.useState(false);
    const [searchText, setSearchText] = react_1.default.useState("");
    const [reason, setReason] = react_1.default.useState(null);
    const [collapsedSections, setCollapsedSections] = react_1.default.useState({});
    const [uncontrolledSelectedValue, setUncontrolledSelectedValue] = react_1.default.useState(null);
    const rawSelectedValue = controlledSelectedValue ?? uncontrolledSelectedValue;
    const setSelectedValue = setControlledSelectedValue ?? setUncontrolledSelectedValue;
    const hasEmptyKeyInList = react_1.default.useMemo(() => {
        return list.some((item) => {
            if (!item || typeof item !== 'object')
                return false;
            if (typeof valueKey === 'function') {
                return valueKey(item) === "";
            }
            if (typeof valueKey === 'string' && valueKey in item) {
                const val = item[valueKey];
                return val === "";
            }
            return false;
        });
    }, [list, valueKey]);
    const selectedValue = react_1.default.useMemo(() => {
        if (rawSelectedValue == null || rawSelectedValue === undefined) {
            return [];
        }
        if (multiple) {
            if (Array.isArray(rawSelectedValue)) {
                return rawSelectedValue
                    .map(String)
                    .filter((v) => v !== "" || hasEmptyKeyInList);
            }
            if (typeof rawSelectedValue === 'string') {
                if (rawSelectedValue === '') {
                    return hasEmptyKeyInList ? [''] : [];
                }
                return rawSelectedValue
                    .split(',')
                    .map((v) => v.trim())
                    .filter((v) => v !== "" || hasEmptyKeyInList);
            }
            return [String(rawSelectedValue)];
        }
        else {
            if (rawSelectedValue === '') {
                return hasEmptyKeyInList ? [''] : [];
            }
            return [String(rawSelectedValue)];
        }
    }, [rawSelectedValue, multiple, list, valueKey, hasEmptyKeyInList]);
    const [selectedLabel, setSelectedLabel] = react_1.default.useState(null);
    const [activeIndex, setActiveIndex] = react_1.default.useState(null);
    const listRef = react_1.default.useRef([]);
    const capsuleRef = react_1.default.useRef(null);
    const leftButtonRef = react_1.default.useRef(null);
    const rightButtonRef = react_1.default.useRef(null);
    const clearButtonRef = react_1.default.useRef(null);
    const triggerButtonRef = react_1.default.useRef(null);
    const inputRef = react_1.default.useRef(null);
    const isOpen = controlledOpen ?? uncontrolledOpen;
    const setIsOpen = setControlledOpen ?? setUncontrolledOpen;
    const backgroundColor = rawBackgroundColor ?? THEME.PrimaryBackgroundLight;
    const toggleSection = react_1.default.useCallback((clientId) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [clientId]: !prev[clientId],
        }));
    }, []);
    react_1.default.useEffect(() => {
        if (isOpen) {
            registerFloating(floatingId);
        }
        else {
            unregisterFloating(floatingId);
            setSearchText("");
        }
        return () => {
            unregisterFloating(floatingId);
        };
    }, [isOpen, floatingId]);
    react_1.default.useEffect(() => {
        setActiveIndex(null);
    }, [searchText]);
    const displayValue = react_1.default.useMemo(() => {
        if (selectedValue == null || selectedValue == undefined)
            return "";
        const values = selectedValue;
        if (values.length === 0) {
            return "";
        }
        if (values.length === 1) {
            const item = list.find((item) => {
                const val = (0, exports.resolveFieldValue)(item, valueKey);
                return String(val) === values[0];
            });
            return item ? String((0, exports.resolveFieldValue)(item, labelKey) || "") : "";
        }
        return (`${values.length} ${t("common:text.itemsSelected")}`);
    }, [list, selectedValue, valueKey, labelKey]);
    const filteredList = react_1.default.useMemo(() => {
        if (disableInternalFilter)
            return list;
        const cleanSearch = (0, exports.normalize)(searchText);
        if (!cleanSearch) {
            return openOnClick ? list : [];
        }
        return list.filter((item) => {
            const fieldsToSearch = searchFields?.length
                ? searchFields
                : Object.keys(item);
            return fieldsToSearch.some((field) => {
                const fieldValue = (0, exports.resolveFieldValue)(item, field);
                if (fieldValue == null)
                    return false;
                if (typeof fieldValue === "boolean") {
                    const tagLabel = (0, exports.formatBooleanTag)(field, fieldValue, booleanTagOverrides);
                    return (0, exports.normalize)(tagLabel).includes(cleanSearch);
                }
                return (0, exports.normalize)(fieldValue).includes(cleanSearch);
            });
        });
    }, [
        openOnClick,
        list,
        searchText,
        searchFields,
        disableInternalFilter,
        booleanTagOverrides
    ]);
    const displayList = react_1.default.useMemo(() => {
        const baseList = disableInternalFilter ? list : filteredList;
        if (!baseList || baseList.length === 0)
            return [];
        const selected = selectedValue;
        if (!groupBy) {
            const ordered = [
                ...baseList.filter((item) => {
                    const val = (0, exports.resolveFieldValue)(item, valueKey);
                    return selected.includes(String(val));
                }),
                ...baseList.filter((item) => {
                    const val = (0, exports.resolveFieldValue)(item, valueKey);
                    return !selected.includes(String(val));
                }),
            ];
            const totalItems = ordered.length;
            return ordered.map((item, idx) => ({
                ...item,
                isHeader: false,
                index: idx,
                posInset: idx + 1,
                setSize: totalItems,
            }));
        }
        const groupsMap = new Map();
        baseList.forEach((item) => {
            const groupUniqueKey = String((0, exports.resolveFieldValue)(item, groupBy));
            const groupTitle = String((0, exports.resolveFieldValue)(item, groupTitleKey || groupBy) || groupUniqueKey);
            if (!groupsMap.has(groupUniqueKey)) {
                groupsMap.set(groupUniqueKey, { title: groupTitle, items: [] });
            }
            groupsMap.get(groupUniqueKey).items.push(item);
        });
        let totalVisibleItems = 0;
        groupsMap.forEach((groupData, groupUniqueKey) => {
            const isCollapsed = Boolean(collapsedSections[groupUniqueKey]);
            if (!isCollapsed) {
                totalVisibleItems += groupData.items.length;
            }
        });
        const flatResult = [];
        let globalIndex = 0;
        let currentPosInSet = 0;
        groupsMap.forEach((groupData, groupUniqueKey) => {
            if (groupData.items.length === 0) {
                return;
            }
            const isCollapsed = Boolean(collapsedSections[groupUniqueKey]);
            flatResult.push({
                isHeader: true,
                groupKey: groupUniqueKey,
                groupTitle: groupData.title,
                rawCount: groupData.items.length,
                isCollapsed,
                index: globalIndex++,
            });
            if (!isCollapsed) {
                const orderedGroupItems = [
                    ...groupData.items.filter((item) => {
                        const val = (0, exports.resolveFieldValue)(item, valueKey);
                        return selected.includes(String(val));
                    }),
                    ...groupData.items.filter((item) => {
                        const val = (0, exports.resolveFieldValue)(item, valueKey);
                        return !selected.includes(String(val));
                    }),
                ];
                orderedGroupItems.forEach((item) => {
                    currentPosInSet++;
                    flatResult.push({
                        ...item,
                        isHeader: false,
                        groupKey: groupUniqueKey,
                        groupTitle: groupData.title,
                        index: globalIndex++,
                        posInSet: currentPosInSet,
                        setSize: totalVisibleItems,
                    });
                });
            }
        });
        return flatResult;
    }, [
        disableInternalFilter,
        list,
        filteredList,
        groupBy,
        groupTitleKey,
        selectedValue,
        valueKey,
        collapsedSections
    ]);
    const selectItem = react_1.default.useCallback((item) => {
        if (!item || item.isHeader)
            return;
        const itemValue = (0, exports.resolveFieldValue)(item, valueKey);
        const itemLabel = (0, exports.resolveFieldValue)(item, labelKey);
        const currentSelected = selectedValue;
        const isCurrentlySelected = currentSelected.includes(String(itemValue));
        if (multiple) {
            const nextValue = isCurrentlySelected
                ? currentSelected.filter((value) => value !== String(itemValue))
                : [...currentSelected, String(itemValue)];
            setActiveIndex(null);
            setSelectedValue(nextValue);
            onSelect?.(nextValue, item);
            if (clearSearchOnSelect) {
                setSearchText("");
            }
        }
        else {
            if (closeOnSelect !== false) {
                setIsOpen(false);
            }
            if (isCurrentlySelected && !required) {
                setSelectedValue(null);
                setSearchText("");
                setReason('item-deselected');
                onSelect?.(null, null);
            }
            else {
                setSelectedValue(itemValue);
                setSearchText(itemLabel || "");
                setReason('item-selected');
                onSelect?.(itemValue, item);
            }
        }
    }, [
        multiple,
        selectedValue,
        setSelectedValue,
        onSelect,
        clearSearchOnSelect,
        closeOnSelect,
        required,
        valueKey,
        labelKey
    ]);
    const pinnedItemIndices = react_1.default.useMemo(() => {
        if (!displayList || displayList.length === 0)
            return [];
        const indices = [];
        for (let i = 0; i < displayList.length; i++) {
            const item = displayList[i];
            if (item.isHeader) {
                indices.push(i);
                continue;
            }
        }
        return indices;
    }, [displayList]);
    return react_1.default.useMemo(() => ({
        list,
        loading,
        valueKey,
        labelKey,
        searchFields,
        groupBy,
        activeColor,
        backgroundColor,
        collapsibleSections,
        toggleSection,
        collapsedSections,
        booleanTagOverrides,
        displayValue,
        displayList,
        pinnedItemIndices,
        isOpen,
        setIsOpen,
        searchText,
        setSearchText,
        reason,
        setReason,
        activeIndex,
        setActiveIndex,
        listRef,
        capsuleRef,
        leftButtonRef,
        rightButtonRef,
        clearButtonRef,
        triggerButtonRef,
        inputRef,
        disabled,
        openOnClick,
        closeWhenTextCleared,
        multiple,
        selectedLabel,
        selectedValue,
        selectItem,
        required,
        searchable,
    }), [
        list,
        loading,
        valueKey,
        labelKey,
        searchFields,
        groupBy,
        activeColor,
        backgroundColor,
        collapsibleSections,
        toggleSection,
        collapsedSections,
        booleanTagOverrides,
        displayValue,
        displayList,
        pinnedItemIndices,
        isOpen,
        setIsOpen,
        reason,
        searchText,
        activeIndex,
        disabled,
        openOnClick,
        closeWhenTextCleared,
        multiple,
        selectedLabel,
        selectedValue,
        selectItem,
        required,
        searchable,
    ]);
}
exports.SearchableContext = react_1.default.createContext(null);
const useOptionalSearchable = () => react_1.default.useContext(exports.SearchableContext);
exports.useOptionalSearchable = useOptionalSearchable;
const useSearchable = (customContext) => {
    const defaultContext = react_1.default.useContext(exports.SearchableContext);
    const context = customContext ?? defaultContext;
    if (!context) {
        throw new Error('useSearchable must be used inside <SearchableInput> or provided with an explicit context');
    }
    return context;
};
exports.useSearchable = useSearchable;
exports.FloatingSearchContext = react_1.default.createContext(null);
const useOptionalFloatingSearch = () => react_1.default.useContext(exports.FloatingSearchContext);
exports.useOptionalFloatingSearch = useOptionalFloatingSearch;
const useFloatingSearch = () => {
    const context = (0, exports.useOptionalFloatingSearch)();
    if (!context) {
        throw new Error('useFloatingSearch must be used inside <SearchableInput>');
    }
    return context;
};
exports.useFloatingSearch = useFloatingSearch;
